import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { arrayRemove, arrayUnion, deleteDoc, doc, getDoc, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useEffect, useState } from "react";
import {
	ButtonGroup,
	Card,
	Col,
	Container,
	Dropdown,
	DropdownButton,
	Row,
	Toast,
	ToastContainer,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { db, storage } from "../../firebase"; // Adjust path if necessary
import useImageUpload from "../../hooks/useImageUpload";
import EditEventModal from "../../modals/editEventModal";
import IceBreakerModal from "../../modals/whatIsIceBreaker1";
import { formatDateLongEuropean, initialEventData } from "../../utilities/reusableItems";
import IceBreaker from "../icebreaker/IceBreaker";
import Spinner from "../Spinner";
import AttendeeList from "./AttendeeList";
import BigButton from "./BigButton";

const libraries = []; // or ["places"] if you use Places API

const EventDetail = ({ user }) => {
	// Assign as attending or not attending
	const [eventCreator, setEventCreator] = useState(null);
	const { eventId } = useParams();
	const [event, setEvent] = useState(null);
	const eventRef = doc(db, "events", eventId);
	const [showModal, setShowModal] = useState(false);
	const [showIceModal, setShowIceModal] = useState(false);
	const [formData, setFormData] = useState(initialEventData);
	const [showIceBreaker, setshowIceBreaker] = useState(false);
	const [saving, setSaving] = useState(false);
	const [loading, setLoading] = useState(true);
	const [isFavorite, setIsFavorite] = useState(false);
	const [showToast, setShowToast] = useState(false);
	const [toastMessage, setToastMessage] = useState("");

	const handleImageFromUploader = (file) => {
		setFormData((prev) => ({ ...prev, image: file }));
	};

	const navigate = useNavigate(); // 👈 for routing

	const { isLoaded, loadError } = useLoadScript({
		googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
		libraries,
	});
	useEffect(() => {
		const fetchEventDetails = async () => {
			try {
				setLoading(true); // 👈 show spinner
				const eventRef = doc(db, "events", eventId);
				const eventSnap = await getDoc(eventRef);

				if (!eventSnap.exists()) {
					console.log("Event not found");
					return;
				}

				const eventData = eventSnap.data();
				setEvent({ ...eventData, id: eventSnap.id });
				setFormData({ ...eventData });

				if (eventData.userId) {
					const userRef = doc(db, "users", eventData.userId);
					const userSnap = await getDoc(userRef);

					if (userSnap.exists()) {
						setEventCreator(userSnap.data());
					}
				}
			} catch (error) {
				console.error("Error fetching event or user details:", error);
			} finally {
				setLoading(false); // 👈 hide spinner
			}
		};

		fetchEventDetails();
	}, [eventId]);

	useEffect(() => {
		const fetchUserFavorites = async () => {
			if (!user) return;
			try {
				const userRef = doc(db, "users", user.uid);
				const userSnap = await getDoc(userRef);

				if (userSnap.exists()) {
					const favorites = userSnap.data().favorites || [];
					setIsFavorite(favorites.includes(event?.userId));
				}
			} catch (error) {
				console.error("Error fetching user favorites:", error);
			}
		};

		if (event?.userId) {
			fetchUserFavorites();
		}
	}, [event?.userId, user]);

	const handleCancelEvent = async () => {
		try {
			setSaving(true);
			await updateDoc(eventRef, { eventStatus: "canceled" });
			setEvent((prev) => ({ ...prev, eventStatus: "canceled" }));
			setFormData((prev) => ({ ...prev, eventStatus: "canceled" }));
			handleNotAttend();
			alert("Event has been canceled.");
			setShowModal(false);
		} catch (error) {
			console.error("Failed to cancel event:", error);
		} finally {
			setSaving(false);
		}
	};

	const handleActivateEvent = async () => {
		try {
			setSaving(true);
			await updateDoc(eventRef, { eventStatus: "active" });
			setEvent((prev) => ({ ...prev, eventStatus: "active" }));
			setFormData((prev) => ({ ...prev, eventStatus: "active" }));
			handleAttend();
			alert("Event has been activated.");
			setShowModal(false);
		} catch (error) {
			console.error("Failed to activate event:", error);
		} finally {
			setSaving(false);
		}
	};

	const handleDeleteEvent = async () => {
		if (!window.confirm("Are you sure you want to delete this event? This cannot be undone.")) {
			return;
		}
		try {
			setSaving(true);
			await deleteDoc(eventRef);
			handleNotAttend();
			alert("Event deleted successfully.");
			navigate("/events"); // redirect after deletion
		} catch (error) {
			console.error("Failed to delete event:", error);
		} finally {
			setSaving(false);
		}
	};

	const handleCreatorClick = () => {
		if (event && event.userId) {
			navigate(`/profile/${event.userId}`);
		}
	};

	const handleEdit = () => setShowModal(true);
	const handleClose = () => setShowModal(false);

	const handleChange = (e) => {
		setFormData((prev) => ({
			...prev,
			[e.target.name]: e.target.value,
		}));
	};

	const { resizeImage } = useImageUpload();

	const handleSave = async () => {
		const eventRef = doc(db, "events", eventId);
		setSaving(true); // ✅ start spinner

		try {
			let imageUrl = event.imageUrl || ""; // fallback to existing if no new upload
			if (formData.image) {
				const resizedImage = await resizeImage(formData.image);
				const imageRef = ref(storage, `events/${user.uid}/${formData.image.name}`);
				await uploadBytes(imageRef, resizedImage);
				imageUrl = await getDownloadURL(imageRef);
			}

			console.log(formData);

			await updateDoc(eventRef, {
				title: formData.title,
				date: formData.date,
				startTime: formData.startTime,
				endTime: formData.endTime,
				imageUrl,
				address: formData.address,
				lat: formData.lat,
				lng: formData.lng,
				placeId: formData.placeId,
				staticMapUrl: formData.staticMapUrl,
				name: formData.name,
				eventStatus: formData.eventStatus,
				details: formData.details,
			});

			setEvent((prev) => ({ ...prev, ...formData, imageUrl }));
			setShowModal(false);
			console.log("Event updated");
		} catch (error) {
			console.error("Failed to update event:", error);
		} finally {
			setSaving(false); // ✅ stop spinner
		}
	};

	if (loading) return <Spinner />;

	async function handleAttend() {
		try {
			await updateDoc(eventRef, {
				attending: arrayUnion(user.uid),
				notAttending: arrayRemove(user.uid),
			});
			console.log("User added to attending list.");
			setEvent((prev) => ({
				...prev,
				attending: [...(prev.attending || []), user.uid],
				notAttending: (prev.notAttending || []).filter((id) => id !== user.uid),
			}));
		} catch (error) {
			console.error("Error updating attendance:", error);
		}
	}

	async function handleNotAttend() {
		try {
			await updateDoc(eventRef, {
				attending: arrayRemove(user.uid),
				notAttending: arrayUnion(user.uid),
			});
			console.log("User marked as not attending.");
			setEvent((prev) => ({
				...prev,
				attending: (prev.attending || []).filter((id) => id !== user.uid),
				notAttending: [...(prev.notAttending || []), user.uid],
			}));
		} catch (error) {
			console.error("Error updating not attending status:", error);
		}
	}

	const isOwner = user && event.userId === user.uid;

	const handleOpenIceBreaker = () => setshowIceBreaker(true);
	const handleCloseIcebreaker = () => setshowIceBreaker(false);

	const toggleFavorite = async () => {
		if (!user || !event?.userId) return;

		const userRef = doc(db, "users", user.uid);

		try {
			if (isFavorite) {
				// Remove organizer UID
				await updateDoc(userRef, {
					favorites: arrayRemove(event.userId),
				});
				setIsFavorite(false);
				setToastMessage("Organizer removed from your favourites");
			} else {
				// Add organizer UID
				await updateDoc(userRef, {
					favorites: arrayUnion(event.userId),
				});
				setIsFavorite(true);
				setToastMessage("Organizer saved to your favourites");
			}
			setShowToast(true);
		} catch (error) {
			console.error("Error updating favorites:", error);
		}
	};

	return (
		<>
			<Container>
				<Row className="justify-content-center">
					<Col xs={12} md={6}>
						<div className="d-flex justify-content-between align-items-center mb-2">
							<BigButton title="Back to Events" url="/events" icon="bi bi-arrow-left-circle" />

							<div className="text-muted">
								{event.attending?.includes(user.uid) && (
									<div className="d-flex align-items-center me-3 mt-2">
										<i className="bi bi-check-circle-fill me-2 attending-display text-success"></i>
										<span className="text-success">You're going</span>
									</div>
								)}
								{event.notAttending?.includes(user.uid) && (
									<div className="d-flex align-items-center me-3 mt-2">
										<i className="bi bi-x-circle-fill me-2 attending-display text-danger"></i>
										<span className="text-danger">Not going</span>
									</div>
								)}
							</div>
						</div>
						<h2 className="mb-3 d-flex align-items-center">
							{event.eventStatus === "canceled" && (
								<>
									<i className="bi bi-x-circle-fill text-danger me-2"></i>
									<span className="text-danger fw-bold me-2">EVENT CANCELLED:</span>
								</>
							)}
							{event.title}
						</h2>
						<img
							src={event.imageUrl || "/images/placeholder.png"}
							alt="Event"
							className="img-fluid rounded mb-3"
							style={{ maxHeight: "400px", objectFit: "cover" }}
						/>

						{/* Event Buttons */}
						<Col>
							<div className="row g-2">
								<div className="d-flex align-items-center gap-2">
									<DropdownButton
										as={ButtonGroup}
										variant={event.attending?.includes(user.uid) ? "success" : "primary"}
										className="flex-grow-1"
										title={
											event.attending?.includes(user.uid) ? (
												<>
													<i
														className="bi bi-check me-2"
														style={{ fontSize: "1.5rem", lineHeight: 0, verticalAlign: "middle" }}
													></i>
													<span style={{ verticalAlign: "middle" }}>Change RSVP</span>
												</>
											) : (
												<>Attend</>
											)
										}
									>
										<Dropdown.Item onClick={handleAttend} disabled={event.attending?.includes(user.uid)}>
											<i
												className="bi bi-check me-2 text-success"
												style={{ fontSize: "1.5rem", lineHeight: 0, verticalAlign: "top" }}
											></i>
											<span className="text-success">Going</span>
										</Dropdown.Item>
										<Dropdown.Item onClick={handleNotAttend} disabled={event.notAttending?.includes(user.uid)}>
											<i
												className="bi bi-x me-2 text-danger"
												style={{ fontSize: "1.5rem", lineHeight: 0, verticalAlign: "top" }}
											></i>
											<span className="text-danger">Not going</span>
										</Dropdown.Item>
									</DropdownButton>

									<Dropdown align="end">
										<Dropdown.Toggle variant="primary" id="more-options" bsPrefix="btn">
											<i className="bi bi-three-dots-vertical"></i>
										</Dropdown.Toggle>
										<Dropdown.Menu>
											<Dropdown.Item onClick={handleOpenIceBreaker} disabled={!event.attending?.includes(user.uid)}>
												<i className="bi bi-chat-dots me-2"></i>Ice Breaker
											</Dropdown.Item>
											<Dropdown.Item onClick={() => setShowIceModal(true)}>
												<i className="bi bi-info-circle me-2"></i>What is Ice Breaker?
											</Dropdown.Item>
											<Dropdown.Divider />
											<Dropdown.Item onClick={handleEdit} disabled={!isOwner}>
												<i className="bi bi-pencil-fill me-2"></i>Edit Event
											</Dropdown.Item>
										</Dropdown.Menu>
									</Dropdown>
								</div>
							</div>
							{/* AttendeeList */}
							<div className="row mt-2">
								<div className="col-12">
									<AttendeeList attendeeIds={event.attending} isAttending={event.attending?.includes(user.uid)} />
								</div>
							</div>

							<div className="my-3">
								<Row className="mb-3">
									<Col xs={6}>
										<Card className="h-100 shadow-sm">
											<Card.Body>
												<Card.Title className="fs-6 text-muted">
													<i className="bi bi-calendar-event me-2"></i> Date
												</Card.Title>
												<Card.Text>{formatDateLongEuropean(event.date)}</Card.Text>
											</Card.Body>
										</Card>
									</Col>
									<Col xs={6}>
										<Card className="h-100 shadow-sm">
											<Card.Body>
												<Card.Title className="fs-6 text-muted">
													<i className="bi bi-clock me-2"></i> Time
												</Card.Title>
												<Card.Text>
													{event.startTime ? (
														<>
															{event.startTime}
															{event.endTime && <> - {event.endTime}</>}
														</>
													) : (
														event.time || "TBC"
													)}
												</Card.Text>
											</Card.Body>
										</Card>
									</Col>
								</Row>

								{event.name && (
									<Card className="mb-3 shadow-sm">
										<Card.Body>
											<Card.Title className="fs-6 text-muted">
												<i className="bi bi-building me-2"></i> Venue Name
											</Card.Title>
											<Card.Text>{event.name}</Card.Text>
										</Card.Body>
									</Card>
								)}

								<Card className="mb-3 shadow-sm">
									<Card.Body>
										<Card.Title className="fs-6 text-muted">
											<i className="bi bi-geo-alt me-2"></i> Location
										</Card.Title>
										<Card.Text>{event.address}</Card.Text>

										{event.lat && event.lng && (
											<div
												style={{ height: "300px", width: "100%", borderRadius: "4px", overflow: "hidden" }}
												className="mt-3"
											>
												{loadError ? (
													<div>Error loading map</div>
												) : !isLoaded ? (
													<Spinner />
												) : (
													<GoogleMap
														center={{ lat: event.lat, lng: event.lng }}
														zoom={15}
														mapContainerStyle={{ height: "100%", width: "100%" }}
													>
														<Marker position={{ lat: event.lat, lng: event.lng }} />
													</GoogleMap>
												)}
											</div>
										)}
									</Card.Body>
								</Card>
							</div>
						</Col>

						{eventCreator && (
							<Card
								className="position-relative shadow-sm mb-3"
								style={{ cursor: "pointer" }}
								onClick={handleCreatorClick}
							>
								<Card.Body>
									<Card.Title className="fs-6 text-muted">
										<i className="bi bi-person-raised-hand me-2"></i>Organiser
									</Card.Title>
									<div className="d-flex w-100">
										{/* 👤 Host profile image */}
										<div
											style={{
												width: "40px",
												height: "40px",
												borderRadius: "50%",
												overflow: "hidden",
												flexShrink: 0,
											}}
											className="me-3"
										>
											<img
												src={eventCreator.photoURL || "/images/no-profile-picture.png"}
												alt="Host"
												style={{
													width: "100%",
													height: "100%",
													objectFit: "cover", // ensures no stretching
												}}
											/>
										</div>

										{/* 📝 Host details */}
										<div>
											<strong>{eventCreator.displayName}</strong>
											<p className="mb-0 text-muted" style={{ fontSize: "0.9rem" }}>
												{eventCreator?.interestText
													? (() => {
															const words = eventCreator.interestText.trim().split(/\s+/);
															return words.length > 10
																? words.slice(0, 10).join(" ") + "..."
																: eventCreator.interestText;
														})()
													: null}
											</p>
										</div>

										{/* save organiser */}
										<div
											className="ms-auto"
											onClick={(e) => {
												e.stopPropagation();
												toggleFavorite();
											}}
										>
											<i
												className={`bi bi bi-bookmark${isFavorite ? "bi bi-bookmark-check-fill my-color" : ""}`}
												style={{ fontSize: "1.5rem", cursor: "pointer" }}
											></i>
										</div>
									</div>
									<ToastContainer position="bottom-start" className="p-3">
										<Toast show={showToast} onClose={() => setShowToast(false)} delay={3000} autohide bg="success">
											<Toast.Body className="text-white">
												{" "}
												<i className="bi bi-check-circle-fill me-2"></i>
												{toastMessage}
											</Toast.Body>
										</Toast>
									</ToastContainer>
								</Card.Body>
							</Card>
						)}

						<Card className="shadow-sm mb-3 position-relative">
							<Card.Body>
								{/* Main Card Content */}
								<Card.Title className="fs-6 text-muted">
									<i className="bi bi-info-circle me-2"></i> Details
								</Card.Title>
								<div dangerouslySetInnerHTML={{ __html: event.details }} />
							</Card.Body>
						</Card>
					</Col>
				</Row>
			</Container>
			<IceBreakerModal show={showIceModal} onHide={() => setShowIceModal(false)}></IceBreakerModal>

			<EditEventModal
				show={showModal}
				onHide={handleClose}
				event={event}
				formData={formData}
				saving={saving}
				onChange={handleChange}
				onSave={handleSave}
				onCancelEvent={handleCancelEvent}
				onActivateEvent={handleActivateEvent}
				onDeleteEvent={handleDeleteEvent}
				onCopyEvent={() => navigate("/create-event", { state: { copiedEvent: event } })}
				onImageChange={handleImageFromUploader}
				setFormData={setFormData}
			/>

			<IceBreaker
				show={showIceBreaker}
				onClose={handleCloseIcebreaker}
				title="Ice Breaker"
				attendees={event.attending}
			></IceBreaker>
		</>
	);
};

export default EventDetail;
